import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import monitorApi from "../../api/monitorApi";
import GraphMap from "../../components/GraphMap/GraphMap";
import Header from "../../components/Header/Header";
import MapDescription from "../../components/MapDescription/MapDescription";
import ShutdownAddNav from "../../components/ShutdownAddNav/ShutdownAddNav";
import { useSiteData } from "../../SiteDataContext";
import {
  MapDataType,
  RegionListType,
  ShutdownTypesType,
} from "./ShutdownPublishPageTypes";

function ShutdownPublishPage() {
  const { t } = useTranslation();
  const { authState } = useSiteData();

  const getYesterday = () => {
    const date_now = new Date();
    const warsawDate = new Date(
      date_now.toLocaleString("en-US", { timeZone: "Europe/Warsaw" }),
    );
    warsawDate.setDate(warsawDate.getDate() - 1);

    const year = warsawDate.getFullYear();
    const month = String(warsawDate.getMonth() + 1).padStart(2, "0");
    const day = String(warsawDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getYesterday());

  const [mapDataWhitelists, setMapDataWhitelists] = useState<MapDataType>([]);
  const [mapDataShutdowns, setMapDataShutdowns] = useState<MapDataType>([]);

  const [shutdownTypesWhitelists, setShutdownTypesWhitelists] =
    useState<ShutdownTypesType>([]);
  const [shutdownTypesShutdowns, setShutdownTypesShutdowns] =
    useState<ShutdownTypesType>([]);

  const [regionsList, setRegionsList] = useState<RegionListType>([]);

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);

  const fetchMapData = useCallback(() => {
    const queryParams: Record<string, string> = {};
    if (selectedDate) {
      queryParams["map_data_start"] = selectedDate;

      const startDateObj = new Date(selectedDate);
      startDateObj.setDate(startDateObj.getDate() + 1);
      const endDate = startDateObj.toISOString().split("T")[0]; // Format as YYYY-MM-DD
      queryParams["map_data_end"] = endDate;
    }

    monitorApi
      .getStats(queryParams)
      .then((response) => {
        const map_data_whitelists = response.data.map_data_whitelists;
        const map_data_shutdowns = response.data.map_data_shutdowns;
        const shutdown_types_whitelists = response.data.sd_type_whitelists;
        const shutdown_types_shutdowns = response.data.sd_type_shutdowns;
        const regions_list = response.data.regions_list;

        setMapDataWhitelists(map_data_whitelists);
        setMapDataShutdowns(map_data_shutdowns);

        setShutdownTypesWhitelists(shutdown_types_whitelists);
        setShutdownTypesShutdowns(shutdown_types_shutdowns);

        setRegionsList(regions_list);
      })
      .catch((error) => {
        console.info("There was an error fetching the map data!", error);
      });
  }, [selectedDate]);

  useEffect(() => {
    fetchMapData();
  }, [selectedDate, fetchMapData]);

  const publishMapData = useCallback(async () => {
    if (mapDataWhitelists.length === 0 && mapDataShutdowns.length === 0) {
      setPublishMessage("No map data to publish");
      return;
    }

    setIsPublishing(true);
    setPublishMessage(null);

    try {
      const transformedMapDataWhitelists = mapDataWhitelists.map((item) => ({
        name: item.name,
        amount_of_incidents: item.amount_of_incidents,
        sd_type: item.sd_type,
      }));

      const transformedMapDataShutdowns = mapDataShutdowns.map((item) => ({
        name: item.name,
        amount_of_incidents: item.amount_of_incidents,
        sd_type: item.sd_type,
      }));

      const payload = {
        note: "",
        map_state: {
          whitelists: {
            regions: transformedMapDataWhitelists,
            sd_types: shutdownTypesWhitelists,
          },
          shutdowns: {
            regions: transformedMapDataShutdowns,
            sd_types: shutdownTypesShutdowns,
          },
        },
        map_date: selectedDate,
      };

      const response = await monitorApi.publishShutdownMapReport(payload);

      if (response.status === 201) {
        setPublishMessage("Map data published successfully!");
      } else {
        setPublishMessage("Failed to publish map data");
      }
    } catch (error: any) {
      console.info("Error publishing map data:", error);
      if (error.response?.data?.message) {
        setPublishMessage(`Error: ${error.response.data.message}`);
      } else {
        setPublishMessage("Failed to publish map data");
      }
    } finally {
      setIsPublishing(false);
    }
  }, [
    mapDataWhitelists,
    mapDataShutdowns,
    selectedDate,
    shutdownTypesWhitelists,
    shutdownTypesShutdowns,
  ]);

  return (
    <main className="w-full">
      <Header />
      <ShutdownAddNav />

      <div
        data-testid="shutdown-publish-page"
        className="p-[1rem] md:p-[2rem] md:mx-60 sm:mx-5 mt-10 flex flex-col w-auto"
      >
        <div className="max-w-lg mx-auto mb-4">
          <h2 className="text-xl font-semibold">
            {t("publish.title", "Publish")}
          </h2>
        </div>

        <div className="mb-3 flex flex-col md:flex-row gap-3 md:gap-4 md:items-end md:justify-between">
          <div className="flex gap-2 items-end">
            <div className="flex flex-col">
              <label className="text-sm font-medium mb-1">Select Date:</label>
              <input
                className="p-2 bg-slate-100 border rounded"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="shutdowns-date-input"
              />
            </div>
            <button
              onClick={() => {
                setSelectedDate(getYesterday());
              }}
              className="bg-blue-300 hover:bg-blue-400 rounded px-3 py-2 text-sm h-10"
              title="Reset date to yesterday"
            >
              Reset
            </button>
          </div>

          <button
            onClick={publishMapData}
            disabled={isPublishing || !authState.isAuthenticated}
            className={`${
              isPublishing || !authState.isAuthenticated
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600"
            } text-white rounded px-4 py-2 text-sm h-10`}
            title="Publish shutdown data"
          >
            {isPublishing ? "Publishing..." : "Publish"}
          </button>
        </div>

        {publishMessage && (
          <div
            className={`mb-4 p-3 rounded ${
              publishMessage.includes("successfully")
                ? "bg-green-100 text-green-700 border border-green-300"
                : "bg-red-100 text-red-700 border border-red-300"
            }`}
          >
            {publishMessage}
          </div>
        )}

        <div className="w-full pl-1-25 bg-cream-light br-1-25 mb-8 w-full p-2-875 pt-2-5 md:pt-3 pb-3 md:plr-3">
          <h3 className="md:f-size-3-125 f-size-1-5 font-bold mb-0-8 md:mb-0-8 text-center md:lt-0-08">
            Карта шатдаунов
          </h3>

          <div className="color-green text-2xl fw-700 text-center mb-1-5">
            {t("stat.map.subtitle", "Network outages by region")}
          </div>

          <GraphMap
            vals={mapDataShutdowns}
            shutdown_types={shutdownTypesShutdowns}
            regions_list={regionsList}
          />

          <MapDescription
            shutdown_types={shutdownTypesShutdowns}
            display_type={1}
            placeholder="Недостаточно данных"
          />
        </div>

        <div className="w-full pl-1-25 bg-cream-light br-1-25 mb-8 w-full p-2-875 pt-2-5 md:pt-3 pb-3 md:plr-3">
          <h3 className="md:f-size-3-125 f-size-1-5 font-bold mb-0-8 md:mb-0-8 text-center md:lt-0-08">
            Карта белых списков
          </h3>

          <div className="color-green text-2xl fw-700 text-center mb-1-5">
            {t("stat.map.subtitle", "Network outages by region")}
          </div>

          <GraphMap
            vals={mapDataWhitelists}
            shutdown_types={shutdownTypesWhitelists}
            regions_list={regionsList}
          />

          <MapDescription
            shutdown_types={shutdownTypesWhitelists}
            display_type={1}
            placeholder="Недостаточно данных"
          />
        </div>
      </div>
    </main>
  );
}

export default ShutdownPublishPage;
